import { Box, Button, Chip, styled, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useCallback, useMemo, useState } from 'react';
import { Table } from '@tanstack/table-core';
import { TableSkeletonLoader } from '../../../../components/SkeletonLoader/Table.SkeletonLoader';
import { AvatarCell } from '../../../../components/Table/CellRenderers/AvatarCell';
import { Typography } from '../../../../components/Typography/Typography';
import { useTimesheetStatusColor } from '../../../../hooks/useTimesheetStatusColor';
import { useSingleTimesheet } from '../../../../queries/hooks/useSingleTimesheet';
import {
  LoadingId,
  Timesheet,
  TimesheetRow,
  TimesheetStatus,
  TimesheetStatusLabel,
  WeeklyTimesheet,
} from '../../../../types';
import { ReactComponent as CheckIcon } from '../../../../assets/icons/check-icon-default.svg';
import { useLoadingBar } from '../../../../hooks/useLoadingBar';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import { extractErrorMessage } from '../../../../utils/errorNotifier';
import { useClearTimesheetTable } from '../../../../queries/hooks/useClearTimesheetTable';
import { SubmitTimesheetModal } from '../SubmitTimesheetModal';
import { TimesheetNavigation } from './TimesheetNavigation';
import { useLocation } from 'react-router';
import { ROUTES } from '../../../../constants/routes';
import { isSmallScreenState, userState } from '../../../../state/UIState';
import { useAtomValue } from 'jotai';
import { SINGLE_TS_MOCK_DATA } from '../../../../components/Onboarding/OnboardingMockData';
import { getOpTsStatusLabel } from '../../../../utils/tsMappers';
import { loadingClearColumn } from '../../../../state/UITimesheetsState';
import { ConfirmWithdrawModal } from '../ConfirmWithdrawModal';
import { useTimesheetRouteId } from '../../context/TimesheetRouteIdContext';
import { formatWeekName, getWeekStatus } from '../../../../utils/formatters';
import { getWeeklyTimesheetWeekStart } from '../../../../utils/getWeeklyTimesheetWeekStart';
import { isPendingAccountantTimesheetStatus } from '../../../../utils/copyRowsBetweenTimesheets';

type Props = {
  table: Table<TimesheetRow>;
  data: TimesheetRow[];
  updateOnboardingData: boolean;
  /** Stacked month view: compact week title + actions (no avatar / week chevrons). */
  embeddedInMonthView?: boolean;
  /** Submitted week stack on month page: title only, no Clear/Submit. */
  readOnlySubmitted?: boolean;
  /** Month list row: stabilizes Withdraw while `/timesheet/:id` refetches. */
  monthListTimesheet?: Timesheet;
};

function EmbeddedWeekTitle({ timesheet }: { timesheet: Timesheet | WeeklyTimesheet }) {
  const { colors } = useTheme();
  const weekStart = getWeeklyTimesheetWeekStart(timesheet);
  const weekLength = timesheet.weekDates.length + timesheet.holidays.length;
  const calendarStatus = getWeekStatus(timesheet.weekStart);
  const isTSRejected = timesheet.status === TimesheetStatus.REJECTED;
  const isNewWeek = !isTSRejected && calendarStatus === 'New';
  const isOverdue = !isTSRejected && calendarStatus === 'Overdue';

  const cellValue = formatWeekName(weekStart, weekLength);
  const showAccountantBadge = !!timesheet.submittedByAccountant;

  const chipLabel = isTSRejected
    ? String(getOpTsStatusLabel(timesheet.status as TimesheetStatus))
    : calendarStatus;

  const statusPillSx = isTSRejected
    ? {
        backgroundColor: alpha(colors.iconStatus.error, 0.12),
        color: colors.textStatus.error,
        border: `1px solid ${colors.iconStatus.error}`,
      }
    : isNewWeek
    ? {
        backgroundColor: colors.surfaceStatus.active,
        color: colors.textStatus.active,
        border: `1px solid ${colors.textStatus.active}`,
      }
    : isOverdue
    ? {
        backgroundColor: '#3D2E1A',
        color: '#E8A54B',
        border: '1px solid rgba(232, 165, 75, 0.35)',
      }
    : {
        backgroundColor: alpha(colors.text.main, 0.06),
        color: colors.text.secondary,
        border: `1px solid ${colors.border.default}`,
      };

  return (
    <Box
      minHeight={36}
      display='flex'
      flexDirection='row'
      alignItems='center'
      gap={1.25}
      flex={1}
      minWidth={0}
    >
      <Typography
        variant='body'
        color={colors.text.main}
        style={{ fontWeight: 600, fontSize: '15px', letterSpacing: '0.02em' }}
      >
        {cellValue}
      </Typography>
      {!!chipLabel && (
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 22,
            px: '8px',
            borderRadius: '6px',
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1,
            flexShrink: 0,
            ...statusPillSx,
          }}
        >
          {chipLabel}
        </Box>
      )}
      {showAccountantBadge && <AccountantSubmittedBadge />}
    </Box>
  );
}

/** Marks a week that was filled/submitted by an accountant on behalf of the owner. */
function AccountantSubmittedBadge() {
  const { colors } = useTheme();
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: '8px',
        borderRadius: '6px',
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 1,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        backgroundColor: alpha(colors.textAccent.default, 0.12),
        color: colors.textAccent.default,
        border: `1px solid ${alpha(colors.textAccent.default, 0.35)}`,
      }}
    >
      Submitted by Accountant
    </Box>
  );
}

export function SingleTimesheetHeader({
  table,
  data,
  updateOnboardingData,
  embeddedInMonthView = false,
  readOnlySubmitted = false,
  monthListTimesheet,
}: Props) {
  const user = useAtomValue(userState);
  const { colors } = useTheme();
  const { pathname } = useLocation();
  const id = useTimesheetRouteId();
  const isOnboarding = id === 'demo';
  const { pushErrorToast, pushSuccessToast } = useToastMessage();
  const { startLoading, stopLoading } = useLoadingBar();
  const [submitTimesheetOpen, setSubmitTimesheetOpen] = useState(false);
  const [confirmWithdrawModalOpen, setConfirmWithdrawModalOpen] = useState(false);
  const isLoadingClearColumn = useAtomValue(loadingClearColumn);
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const { data: singleTimesheet, isLoading: dataIsLoading } = useSingleTimesheet({
    id: Number(id),
    enabled: !isOnboarding,
  });

  const expectedSum = useMemo(() => {
    if (!singleTimesheet) return 0;
    return (singleTimesheet?.weekDates.length + singleTimesheet?.holidays.length) * 100;
  }, [singleTimesheet]);

  const tsStatusLabel = useMemo(() => {
    if (!singleTimesheet) return;
    return getOpTsStatusLabel(singleTimesheet?.status);
  }, [singleTimesheet]);

  const { mutate: onClearTable, isLoading: loadingClearTable } = useClearTimesheetTable();

  const toggleSubmitTsOpen = useCallback(() => {
    setSubmitTimesheetOpen((prev) => !prev);
  }, []);

  const toggleWithdrawModalOpen = useCallback(() => {
    setConfirmWithdrawModalOpen((prev) => !prev);
  }, []);

  const isSubmitDisabled = useMemo(() => {
    if (updateOnboardingData) return false;
    else if (loadingClearTable) return true;
    else if (isLoadingClearColumn) return true;
    const accessors = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const cellValues: number[] = table
      .getFilteredRowModel()
      .rows.flatMap((row) => accessors.flatMap((day) => row.getValue(day)));
    const columnTotals: number[] = [];
    accessors.forEach((accessor) => {
      const dayTotal = table
        .getFilteredRowModel()
        .rows.reduce((total, row) => total + Number(row.getValue(accessor)), 0);
      columnTotals.push(dayTotal);
    });
    if (columnTotals.some((total) => total > 100)) {
      return true;
    }
    if (cellValues.some((cell) => cell > 100)) {
      return true;
    }
    let sum = 0;
    accessors.forEach((accessor) => {
      const total = table.getFilteredRowModel().rows.reduce((total, row) => {
        return total + Number(row.getValue(accessor));
      }, 0);
      sum += total;
    });
    return sum !== expectedSum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table, data, loadingClearTable, isOnboarding, isLoadingClearColumn]);

  const statusColor = useTimesheetStatusColor(
    isOnboarding ? (SINGLE_TS_MOCK_DATA.status as TimesheetStatusLabel) : tsStatusLabel
  );

  /**
   * May the current user change this sheet's submission state (Submit / Withdraw)? Own sheets always.
   * On someone else's sheet: an Accountant filling an OP's month does submit on their behalf, and
   * (SVP-820) so does a submitter's own proxy once that submitter's "can submit timesheets" switch is
   * on — otherwise the proxy only fills. The API enforces the same rule, so this just avoids offering a
   * button that would come back 403.
   */
  const canActOnTimesheet = useCallback(
    (timesheet?: Timesheet | WeeklyTimesheet | null) => {
      const ownerId = timesheet?.user?.id;
      if (!ownerId || ownerId === user?.id) return true;
      if (pathname.includes(ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES)) return true;
      return timesheet?.user?.proxy?.id === user?.id && !!timesheet?.user?.proxyCanSubmit;
    },
    [pathname, user?.id]
  );

  const isSubmitHidden = useMemo(() => {
    if (isOnboarding) return false;
    return (
      (singleTimesheet?.status !== TimesheetStatus.ISSUED &&
        singleTimesheet?.status !== TimesheetStatus.REJECTED) ||
      !canActOnTimesheet(singleTimesheet)
    );
  }, [isOnboarding, singleTimesheet, canActOnTimesheet]);

  const timesheetForWithdraw = useMemo(() => {
    if (embeddedInMonthView && readOnlySubmitted && monthListTimesheet) {
      return singleTimesheet ?? monthListTimesheet;
    }
    return singleTimesheet;
  }, [embeddedInMonthView, readOnlySubmitted, monthListTimesheet, singleTimesheet]);

  const canWithdraw = useMemo(() => {
    if (isOnboarding) return false;
    // SVP-820: withdrawing is the mirror of submitting, so it follows the same permission. Until now it
    // was status-only, which let a proxy pull back a sheet they were never allowed to send.
    return (
      isPendingAccountantTimesheetStatus(timesheetForWithdraw?.status) &&
      canActOnTimesheet(timesheetForWithdraw)
    );
  }, [isOnboarding, timesheetForWithdraw, canActOnTimesheet]);

  const disableClear = useMemo(() => {
    if (isOnboarding) return false;
    return (
      (singleTimesheet?.status !== TimesheetStatus.ISSUED &&
        singleTimesheet?.status !== TimesheetStatus.REJECTED) ||
      dataIsLoading
    );
  }, [isOnboarding, dataIsLoading, singleTimesheet?.status]);

  if (
    !isOnboarding &&
    dataIsLoading &&
    !(embeddedInMonthView && readOnlySubmitted && monthListTimesheet)
  ) {
    return <TableSkeletonLoader />;
  }

  const actionButtons = (
    <>
      {!disableClear && (
        <Button
          variant='text'
          sx={{
            paddingRight: 0,
            flexGrow: embeddedInMonthView ? 0 : isSmallScreen ? 1 : 0,
          }}
          onClick={() => {
            startLoading(LoadingId.clearTable);
            onClearTable(
              {
                timesheetId: Number(id),
              },

              {
                onSuccess: () => {
                  pushSuccessToast({
                    title: 'Timesheet is Cleared',
                    message: `The timesheet has been cleared successfully.`,
                  });
                },
                onError: (error: unknown) => {
                  pushErrorToast({ message: extractErrorMessage(error) });
                },
                onSettled: () => {
                  stopLoading(LoadingId.clearTable);
                },
              }
            );
          }}
        >
          <Typography color={colors.textAccent.default} variant='subtitle1'>
            Clear All Entries
          </Typography>
        </Button>
      )}
      {!isSubmitHidden && (
        <Button
          startIcon={<StyledCheckIcon disabled={isSubmitDisabled} />}
          variant='contained'
          sx={{
            flexGrow: embeddedInMonthView ? 0 : isSmallScreen ? 1 : 0,
          }}
          disabled={isSubmitDisabled}
          onClick={() => {
            toggleSubmitTsOpen();
          }}
          className='btn-submit'
        >
          Submit
        </Button>
      )}
      {canWithdraw && (
        <Button
          variant='contained'
          sx={{
            flexGrow: embeddedInMonthView ? 0 : isSmallScreen ? 1 : 0,
          }}
          onClick={toggleWithdrawModalOpen}
        >
          Withdraw
        </Button>
      )}
    </>
  );

  const embeddedTimesheetForTitle = singleTimesheet ?? monthListTimesheet;

  const monthSubmittedWithdrawButton = canWithdraw ? (
    <Button
      variant='contained'
      sx={{
        flexGrow: 0,
      }}
      onClick={toggleWithdrawModalOpen}
    >
      Withdraw
    </Button>
  ) : null;

  return (
    <>
      <Container isSmallScreen={isSmallScreen} $embedded={embeddedInMonthView}>
        {embeddedInMonthView && !isOnboarding && embeddedTimesheetForTitle ? (
          <EmbeddedMonthHeaderRow>
            <EmbeddedWeekTitle timesheet={embeddedTimesheetForTitle} />
            {!readOnlySubmitted ? (
              <ActionBtnWrap isSmallScreen={isSmallScreen} $embedded={embeddedInMonthView}>
                {actionButtons}
              </ActionBtnWrap>
            ) : (
              monthSubmittedWithdrawButton && (
                <ActionBtnWrap isSmallScreen={isSmallScreen} $embedded={embeddedInMonthView}>
                  {monthSubmittedWithdrawButton}
                </ActionBtnWrap>
              )
            )}
          </EmbeddedMonthHeaderRow>
        ) : (
          <>
            <UserInfoWrap isSmallScreen={isSmallScreen}>
              <AvatarCell
                name={(isOnboarding ? SINGLE_TS_MOCK_DATA : singleTimesheet)?.user?.name || ''}
                avatarBoxStyle={{
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  maxWidth: 34,
                  background: colors.surfaceIndicator.needsReview,
                }}
                avatarLetterStyle={{
                  color: colors.text.main,
                  fontSize: 14,
                }}
              />
              <Box display={'flex'} gap={'8px'} flex={1}>
                <Chip
                  style={{
                    backgroundColor: colors.surfaceStatus.noAction,
                    minWidth: '66px',
                    height: '28px',
                  }}
                  label={
                    <Typography variant='body' color={statusColor}>
                      {isOnboarding ? SINGLE_TS_MOCK_DATA.status : tsStatusLabel}
                    </Typography>
                  }
                />
                {singleTimesheet?.submittedByAccountant && <AccountantSubmittedBadge />}
              </Box>
            </UserInfoWrap>
            <TimesheetNavigation
              OPId={singleTimesheet?.user?.id}
              timesheet={singleTimesheet}
              isOnboarding={isOnboarding}
            />
            <ActionBtnWrap isSmallScreen={isSmallScreen} $embedded={false}>
              {actionButtons}
            </ActionBtnWrap>
          </>
        )}
      </Container>
      {submitTimesheetOpen && (
        <SubmitTimesheetModal
          timesheet={isOnboarding ? SINGLE_TS_MOCK_DATA : singleTimesheet}
          submitTsOpen={submitTimesheetOpen}
          toggleSubmitTimesheet={toggleSubmitTsOpen}
          isSubmitDisabled={isSubmitDisabled}
          skipNavigateBackOnSuccess={embeddedInMonthView}
        />
      )}
      {confirmWithdrawModalOpen && (
        <ConfirmWithdrawModal
          timesheet={
            isOnboarding ? SINGLE_TS_MOCK_DATA : (singleTimesheet ?? monthListTimesheet)
          }
          isOpen={confirmWithdrawModalOpen}
          toggleModal={toggleWithdrawModalOpen}
        />
      )}
    </>
  );
}

const EmbeddedMonthHeaderRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  padding: 4px 16px 0 0;
`;

const Container = styled('div')<{ isSmallScreen: boolean; $embedded?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ $embedded }) => ($embedded ? 'flex-start' : 'center')};
  padding-bottom: ${({ isSmallScreen, $embedded }) =>
    $embedded ? '0' : isSmallScreen ? '8px' : '28px'};
  position: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'static' : isSmallScreen ? 'relative' : 'static'};
`;

const UserInfoWrap = styled('div')<{ isSmallScreen: boolean }>`
  display: flex;
  align-items: center;
  width: ${({ isSmallScreen }) => (isSmallScreen ? '100%' : '35%')};
  gap: 20px;
`;

const ActionBtnWrap = styled('div')<{ isSmallScreen: boolean; $embedded?: boolean }>`
  display: flex;
  justify-content: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'flex-end' : isSmallScreen ? 'flex-start' : 'flex-end'};
  gap: 10px;
  align-items: center;
  width: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'auto' : isSmallScreen ? '95%' : '35%'};
  flex-shrink: 0;
  position: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'static' : isSmallScreen ? 'absolute' : 'static'};
  bottom: 0px;
  margin-left: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'unset' : isSmallScreen ? 'auto' : 'unset'};
  margin-right: ${({ isSmallScreen, $embedded }) =>
    $embedded ? 'unset' : isSmallScreen ? 'auto' : 'unset'};
  padding-bottom: ${({ isSmallScreen, $embedded }) =>
    $embedded ? '0' : isSmallScreen ? '16px' : '0'};
  background-color: ${({ theme, $embedded }) =>
    $embedded ? 'transparent' : theme.colors.surfaceBackground.bg1};
  left: 0;
  right: 0;
  z-index: ${({ $embedded }) => ($embedded ? 'auto' : '99')};
`;

const StyledCheckIcon = styled(CheckIcon)<{ disabled: boolean }>`
  width: 20px;
  height: 20px;
  path {
    fill: ${({ disabled, theme }) =>
      disabled ? theme.colors.icon.disabled : theme.colors.icon.inverse};
  }
`;
